import { AfterViewInit, Component, computed, ElementRef, inject, input, output, viewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Handsontable from "handsontable";
import { CellChange } from "handsontable/settings";
import { ColumnSettings, GridSettings } from "handsontable/settings";

import { environment } from "../../../../core/environments/environment";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language } from "../../../constants/enums";
import { DataEntryDetails } from "../../../models";
import { DataentryService } from "../../../services";

@Component({
  selector: "adaa-kpi-forecasted-values",
  imports: [],
  templateUrl: "./kpi-forecasted-values.html",
  styleUrl: "./kpi-forecasted-values.scss",
})
export class KpiForecastedValuesComponent implements AfterViewInit {
  private readonly _translateService = inject(TranslateService);
  private readonly _dataEntryService = inject(DataentryService);

  forecastedValuesDetails = input.required<DataEntryDetails>();
  valueChanged = output<DataEntryDetails>();

  hotTableContainer = viewChild.required<ElementRef>("hotTableContainer");

  isFormula = computed<boolean>(() => this.forecastedValuesDetails().formula !== Constants.FORMULA_STRING.NOFORMULA);
  metricsLength = computed<number>(() =>
    this.forecastedValuesDetails().metricDetails ? this.forecastedValuesDetails().metricDetails!.length : 0
  );

  private _calcColumnIndex: number = 0;
  hotInstance: Handsontable;

  public ngAfterViewInit(): void {
    const container = this.hotTableContainer().nativeElement;
    const options = this._getOptions();
    this.hotInstance = new Handsontable(container, options);
  }

  private _getOptions(): GridSettings {
    const currentLang = AdaaHelper.getCurrentLang();
    const { columnWidths, columns } = this._getColumns();

    return {
      data: this._getData(),
      rowHeaders: this._getRowHeaders(),
      rowHeaderWidth: 150,
      rowHeights: Constants.GRID_ROW_HEIGHT.DEFAULT,
      colHeaders: !this.isFormula() ? this._getColHeaders() : undefined,
      nestedHeaders: this.isFormula() ? this._getNestedHeaders() : undefined,
      columns: columns,
      colWidths: columnWidths,
      afterChange: (changes, _source) => this._handleCellValueChanged(changes),
      //Same Conf
      stretchH: "none",
      height: "auto",
      viewportColumnRenderingOffset: 999999,
      viewportRowRenderingOffset: 999999,
      hiddenColumns: true,
      layoutDirection: currentLang === Language.Arabic ? "rtl" : "ltr",
      language: currentLang === Language.Arabic ? "ar-AR" : "en-US",
      licenseKey: environment.handsontable_key,
      themeName: "ht-theme-classic",
      afterGetColHeader(col, TH) {
        if (TH) {
          TH.id = `col-header-${col}`;
        }
      },
    };
  }

  private _getColumns() {
    const columnWidths = [];
    const columns: ColumnSettings[] = [];

    if (this.isFormula()) {
      this.forecastedValuesDetails().metricDetails?.forEach((_metric) => {
        columnWidths.push(200);
        columns.push({});
      });
    }

    columnWidths.push(200);
    columns.push({
      readOnly: this.isFormula() ? true : false,
      className: this.isFormula() ? "disabledCell" : undefined,
    });

    return { columnWidths, columns };
  }

  private _getRowHeaders(): string[] {
    const headers: string[] = [];
    this.forecastedValuesDetails().values?.forEach((e) => {
      headers.push(this._dataEntryService.getLabelForPeriodColumn(e, this.forecastedValuesDetails().frequency!));
    });
    return headers;
  }

  private _getColHeaders(): string[] {
    const headers = [this._translateService.instant("forecasted_values.title_single")];

    return headers;
  }

  private _getNestedHeaders() {
    const firstHeader = [
      {
        label: this._translateService.instant("data_entry.actuals"),
        colspan: this.metricsLength(),
      },
      this._translateService.instant("data_entry.calc"),
    ];

    const secondHeader: string[] = [];
    this.forecastedValuesDetails().metricDetails?.forEach((metric) => {
      secondHeader.push(AdaaHelper.getItemValueByToken(metric, "metricName"));
      this._calcColumnIndex++;
    });

    return [firstHeader, secondHeader];
  }

  private _getData() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let innerData: any[] = [];

    if (!this.forecastedValuesDetails().values) return data;

    if (!this.isFormula()) {
      this.forecastedValuesDetails().values?.forEach((forecastedValue) => {
        innerData = [];

        innerData.push(forecastedValue.value);

        data.push(innerData);
      });
    } else {
      this.forecastedValuesDetails().values?.forEach((forecastedValue) => {
        innerData = [];

        forecastedValue.metrics?.forEach((metric) => {
          innerData.push(metric.value);
        });

        //Calc
        innerData.push(
          this._dataEntryService.calcFormulaForecasted(
            forecastedValue.metrics,
            this.forecastedValuesDetails().metricDetails,
            this.forecastedValuesDetails().formula
          )
        );

        data.push(innerData);
      });
    }

    return data;
  }

  private _handleCellValueChanged = (changes: CellChange[] | null) => {
    if (!changes || !this.hotInstance || this.hotInstance.isDestroyed) return;

    changes.forEach((change) => {
      let [row, col, prev, next] = change as [number, number, number | undefined, number | undefined];
      let invalidCell = false;

      row = +row;
      col = +col;

      //If the change was on the calc column skip
      if (this.isFormula() && this._calcColumnIndex === col) return;

      if (!AdaaHelper.isDefinedAndNotEmpty(prev)) prev = undefined;
      if (!AdaaHelper.isDefinedAndNotEmpty(next)) next = undefined;

      //Avoid saving changes on empty cells
      if (!AdaaHelper.isDefined(prev) && !AdaaHelper.isDefined(next)) return;

      //Avoid saving if metric does not exists
      if (!AdaaHelper.isDefined(this.forecastedValuesDetails()?.values![row])) return;

      if (next !== undefined && isNaN(next)) {
        this.hotInstance.setCellMeta(row, col, "valid", false);
        invalidCell = true;
      } else {
        this.hotInstance.removeCellMeta(row, col, "valid");
      }

      const forecastedValue = this.forecastedValuesDetails().values![row];
      if (!forecastedValue) return;

      if (!this.isFormula()) {
        forecastedValue.value = invalidCell ? next : Number(next);
        forecastedValue.invalid = invalidCell;
      } else {
        const metricValue = forecastedValue.metrics![col];

        if (!metricValue) return;
        metricValue.value = invalidCell ? next : Number(next);
        metricValue.invalid = invalidCell;

        const value = this._dataEntryService.calcFormulaForecasted(
          forecastedValue.metrics,
          this.forecastedValuesDetails().metricDetails,
          this.forecastedValuesDetails().formula
        );
        this.hotInstance.setDataAtCell(row, this._calcColumnIndex, value);
      }
    });

    this.hotInstance.render();
    this.valueChanged.emit(this.forecastedValuesDetails());
  };
}
